import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ALL_ROUTES } from 'config/routes';
import permissions from 'config/permissions';
import { checkPermission } from 'helpers/checkPermissions';
import { orderStatusesActions } from 'components/Orders/constants';
import { useDispatch, useSelector } from 'react-redux';
import { enqueueSnackbar, SNACKBAR_VARIANTS, navigateToRoute } from '../../../../redux_store';
import showServerError from '../../../../helpers/showError';
import useOrderMetadata from '../../../../hooks/useOrderMetadata';
import QhsTable from '../../../Shared/Table/Table';
import TableActions from './TableActions/TableActions';
import { getLocalizedDate } from '../../../../helpers/localize';
import TableMenuOptions from './TableActions/TableMenuOptions';
import RowAction from './TableActions/RowAction';

const OrdersTable = ({
  modifiedOrders,
  setOpenRemoveDialog,
  setRemovingOrder,
  dataLoading,
  totalCount,
  onLoadMoreRows,
  sort,
  onSort,
  filter,
  onFilter,
  reloadOrdersList,
  exportItems,
  exportItemsLoadingStatus,
  fetchExportItems,
  columns,
  tableKey,
  hideMenuActions,
  tableTitle,
  fetchError,
}) => {
  const {
    en: { titles: tTitles, errors: tErrors, notifications: tNotifications },
  } = useSelector(state => state.i18n);
  const dispatch = useDispatch();
  const { accountUuid: paramAccountUuid } = useParams();
  const [order, setOrder] = useState({}); // trigger useOrderMetadata
  const {
    metadata: { currentAccount, currentAccountPermissions, childAccountPermissions },
    isDistributionSponsor,
  } = useSelector(state => state.user);

  const { loading: metadataLoading } = useOrderMetadata(
    order.patientUUID,
    order.accountUUID,
    () => {
      showServerError(dispatch, tErrors.noProvidersOnThisAccount);
      setOrder({});
    },
    {
      actionTitle: tTitles.editPatient,
      reorderUuid: order.uuid,
      actionCallback: () => {},
      callback: () => {
        setOrder({});
      },
    }
  );

  const redirectToEditOrder = ({ uuid, accountUUID, patientUUID }) => {
    const hasPermission = checkPermission(
      accountUUID === currentAccount.uuid ? currentAccountPermissions : childAccountPermissions,
      permissions.ORDER_PATCH
    );
    if (hasPermission) {
      const isItChildAccount = accountUUID !== currentAccount.uuid && isDistributionSponsor;
      dispatch(
        navigateToRoute({
          route: {
            path: (isItChildAccount ? ALL_ROUTES.order.editDS : ALL_ROUTES.order.edit).path,
            params: {
              currentAccountUuid: currentAccount.uuid,
              accountUuid: isItChildAccount ? accountUUID : null,
              patientUuid: patientUUID,
              orderUuid: uuid,
            },
            title: tTitles.editOrder,
          },
        })
      );
    } else {
      dispatch(
        enqueueSnackbar({
          message: tNotifications.notPermissionForEditingOrder,
          options: {
            variant: SNACKBAR_VARIANTS.warning,
          },
        })
      );
    }
  };

  const handleRowClick = order => {
    redirectToEditOrder(order);
  };

  const options = useMemo(() => {
    let statuses = orderStatusesActions;
    if (!isDistributionSponsor) {
      statuses = orderStatusesActions.filter(orderStatus => orderStatus.enabledForNonSponsors);
    }
    return statuses.map(orderStatus => ({
      ...orderStatus,
    }));
  }, [isDistributionSponsor]);

  const modifiedExportItems = useMemo(
    () =>
      exportItems &&
      exportItems.map(item => ({
        ...item,
        dob: getLocalizedDate(item.dob, 'UTC'),
        date: getLocalizedDate(item.date),
      })),
    [exportItems]
  );

  return (
    <QhsTable
      columns={columns}
      data={modifiedOrders || []}
      fetchNextPage={onLoadMoreRows}
      isFetching={dataLoading || metadataLoading}
      isLoading={dataLoading && !modifiedOrders}
      totalRowCount={totalCount}
      sorting={sort}
      setSorting={onSort}
      filter={filter}
      setFilters={onFilter}
      tableTitle={tableTitle}
      onRowClick={handleRowClick}
      fetchError={fetchError}
      tableKey={tableKey}
      menuActions={
        !hideMenuActions && (
          <TableActions
            CSVData={modifiedExportItems}
            CSVDataLoadingStatus={exportItemsLoadingStatus}
            fetchCSVData={fetchExportItems}
            itemLength={totalCount}
            sort={sort}
            filter={filter}
          />
        )
      }
      tableMenuPopoverOptions={
        modifiedOrders?.length
          ? setAnchorElMulti => (
              <TableMenuOptions
                filter={filter}
                sort={sort}
                options={options}
                setAnchorElMulti={setAnchorElMulti}
                totalCount={totalCount}
                reloadData={reloadOrdersList}
                accountUuid={paramAccountUuid || currentAccount.uuid}
              />
            )
          : null
      }
      showRefreshTableButton
      reloadData={reloadOrdersList}
      renderRowActionMenuItems={({ row, setAnchorEl }) => (
        <RowAction
          row={row}
          setAnchorEl={setAnchorEl}
          options={options}
          reloadData={reloadOrdersList}
          setOpenRemoveDialog={setOpenRemoveDialog}
          setRemovingOrder={setRemovingOrder}
          redirectToEditOrder={redirectToEditOrder}
          setOrder={setOrder}
        />
      )}
    />
  );
};

export default OrdersTable;
